/**
 * Hook para gestión de tema light/dark con persistencia y auto-detect del OS.
 *
 * Comportamiento:
 *  1. Si el usuario eligió manualmente (existe `localStorage['jidokap-theme']`)
 *     se respeta esa elección.
 *  2. Si no, se lee `prefers-color-scheme` del OS.
 *  3. Si tampoco hay preferencia del OS, default = `light` (más conservador para
 *     primer encuentro con el cliente).
 *
 * El swap se aplica via `<html data-theme="light|dark">`. Las CSS vars
 * (`tokens.css`) escuchan ese atributo y cambian sin reload.
 *
 * Hay un script inline en `index.html` que aplica el tema ANTES de que React
 * monte (anti-flash). Este hook simplemente sincroniza el estado de React con
 * el DOM y permite cambios programáticos vía `setTheme()`.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Theme } from './tokens';

const STORAGE_KEY = 'jidokap-theme';

function readInitial(): Theme {
  if (typeof window === 'undefined') return 'light';
  // 1) elección persistida del usuario
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  // 2) preferencia del OS
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  // 3) default light
  return 'light';
}

function applyToDom(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

export function useTheme(): {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => readInitial());

  // Aplicar al DOM cuando cambia (defensivo — el script inline ya lo hizo al boot)
  useEffect(() => {
    applyToDom(theme);
  }, [theme]);

  // Si el user NO eligió manualmente, escuchar cambios del OS
  useEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return; // user override — no escuchar OS
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      setThemeState(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback((t: Theme) => {
    window.localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'light' ? 'dark' : 'light';
      window.localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  return { theme, setTheme, toggle };
}
